// ── TEST-SITE-ONLY demo field-size control ───────────────────────────────────
// ONE master 104-team list is the single source of truth. Picking a size N takes
// the TOP N teams by qualifying score, re-splits into flights of 8, and produces
// a clean fresh field (no leftover scores, results, brackets, tracking, or
// Calcutta auction data). Nobody is deleted — teams outside N stay in the master
// list (benched) and return whenever a larger N is chosen. Fully reversible: any
// multiple of 8 up to the master size, anytime, always drawn from the master.
//
// HARD GATE: every function refuses unless IS_TEST_SITE, and the UI hides the
// control off-test — it can neither appear nor act on pbccgolf.com.

import { get, ref, set, update } from 'firebase/database'

import { db, tref, updateTenant } from './firebase.js'
import { normalizeTeamKey, generateBracket } from './data.js'
import { IS_TEST_SITE } from '../config/siteMode.js'

function assertTest() {
  if (!IS_TEST_SITE) throw new Error('Demo field size is TEST SITE only — refusing to run.')
}

// Normalize any source (flight team info, registration, or stored master entry)
// into a master record. Keeps the accessToken so a benched team keeps its
// identity when it returns.
function toRecord(src) {
  const name = src?.name || src?.teamName || (src?.players || []).join('/')
  return {
    name,
    players: Array.isArray(src?.players) ? src.players.filter(Boolean) : [],
    qualifyingScore: Number.isFinite(Number(src?.qualifyingScore)) ? Number(src.qualifyingScore) : null,
    accessToken: src?.accessToken || null,
  }
}

// Gather the current roster from the richest available source (flights, else
// registrations), deduped by the same key the build flow uses.
function gatherRoster(tournament) {
  const out = []
  const seen = new Set()
  const add = (rec) => {
    const key = normalizeTeamKey(rec.name)
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push(rec)
  }
  const flights = tournament.flights || {}
  if (Object.keys(flights).length) {
    for (const f of Object.values(flights)) {
      for (const t of Object.values(f?.teams || {})) add(toRecord(t?.info))
    }
  }
  if (!out.length) {
    for (const r of Object.values(tournament.registrations || {})) add(toRecord(r))
  }
  return out
}

// Ensure the master list exists. Captures it ONCE from the current (full) roster;
// never overwrites an existing master, so it always holds all 104. Returns the
// master as an array of records.
export async function ensureDemoMaster(tid) {
  assertTest()
  const snap = await get(tref(`tournaments/${tid}/demoMaster/teams`))
  const existing = snap.val()
  if (existing && Object.keys(existing).length) {
    return Object.values(existing).map(toRecord)
  }
  const tsnap = await get(tref(`tournaments/${tid}`))
  const roster = gatherRoster(tsnap.val() || {})
  const obj = {}
  roster.forEach((rec, i) => { obj[`m_${String(i + 1).padStart(3, '0')}`] = rec })
  await set(tref(`tournaments/${tid}/demoMaster/teams`), obj)
  return roster
}

// Apply a field size: TOP N by qualifying score → flights of 8 → clean field.
export async function setDemoFieldSize(tid, n) {
  assertTest()
  const size = Number(n)
  if (!Number.isFinite(size) || size < 8 || size % 8 !== 0) {
    throw new Error('Field size must be a multiple of 8.')
  }
  const master = await ensureDemoMaster(tid)
  const maxN = Math.floor(master.length / 8) * 8
  const N = Math.min(size, maxN)
  if (N < 8) throw new Error('Not enough teams in the master list.')

  // Top N by qualifying score (lower is better); null scores sort last; stable
  // tiebreak by name so the split is deterministic and repeatable.
  const chosen = master
    .slice()
    .sort((a, b) => {
      const sa = a.qualifyingScore == null ? Infinity : a.qualifyingScore
      const sb = b.qualifyingScore == null ? Infinity : b.qualifyingScore
      if (sa !== sb) return sa - sb
      return String(a.name).localeCompare(String(b.name))
    })
    .slice(0, N)

  const numFlights = N / 8
  const flightName = (i) => (i === 0 ? 'Championship Flight' : `Flight ${i}`)
  const flightId = (i) => `flight${String(i).padStart(2, '0')}`

  const updates = {}
  // Fresh field — clear the whole built + played + auction state (keep master,
  // meta, and Calcutta SETTINGS; only the auction DATA is cleared).
  updates[`tournaments/${tid}/flights`] = null
  updates[`tournaments/${tid}/brackets`] = null
  updates[`tournaments/${tid}/results`] = null
  updates[`tournaments/${tid}/tracking`] = null
  updates[`tournaments/${tid}/teeTimes`] = null
  updates[`tournaments/${tid}/teamTokens`] = null
  updates[`tournaments/${tid}/qualifying`] = null
  updates[`tournaments/${tid}/registrations`] = null
  updates[`tournaments/${tid}/calcutta/bids`] = null
  updates[`tournaments/${tid}/calcutta/live`] = null
  updates[`tournaments/${tid}/calcutta/finished`] = null
  updates[`tournaments/${tid}/calcutta/flightResult`] = null
  updates[`tournaments/${tid}/calcutta/paid`] = null

  // Build flights of 8: top 8 → Championship Flight, next 8 → Flight 1, etc.
  // Seed match-play pairings within each flight (1v8, 2v7, 3v6, 4v5).
  for (let fi = 0; fi < numFlights; fi++) {
    const fid = flightId(fi)
    updates[`tournaments/${tid}/flights/${fid}/info`] = { name: flightName(fi), format: '4-Ball Best Ball' }
    const slice = chosen.slice(fi * 8, fi * 8 + 8)
    const teamIds = slice.map((_, k) => `team_${fi * 8 + k + 1}`)
    slice.forEach((t, k) => {
      const info = {
        name: t.name,
        players: t.players,
        qualifyingScore: t.qualifyingScore,
        qualifyingStatus: 'complete',
        opponentId: teamIds[7 - k], // 1v8, 2v7, 3v6, 4v5
      }
      if (t.accessToken) info.accessToken = t.accessToken
      updates[`tournaments/${tid}/flights/${fid}/teams/${teamIds[k]}/info`] = info
    })
  }

  updates[`tournaments/${tid}/info/stage`] = 'active'
  updates[`tournaments/${tid}/info/status`] = 'open'
  updates[`tournaments/${tid}/info/closedAt`] = null
  updates[`tournaments/${tid}/info/flightsLocked`] = true
  updates[`tournaments/${tid}/info/demoFieldSize`] = N
  await updateTenant(updates)

  // Regenerate a fresh bracket per flight (seeds from the opponentId pairings).
  const bracketDays = Number((await get(tref(`tournaments/${tid}/info/bracketDays`))).val()) || 3
  for (let fi = 0; fi < numFlights; fi++) {
    try { await generateBracket(tid, flightId(fi), bracketDays) } catch { /* tolerate */ }
  }
  return { size: N, numFlights }
}
